import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, TextField } from "@mui/material";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";

const BRAND_GREEN = "#3BA666";
const BRAND_GREEN_HOVER = "#2e7d32";

const greenOutlinedSx = {
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: BRAND_GREEN,
        },
        "&:hover fieldset": {
            borderColor: BRAND_GREEN_HOVER,
        },
        "&.Mui-focused fieldset": {
            borderColor: BRAND_GREEN,
            borderWidth: 2,
        },
    },
};

/**
 * Browse-style search field with search icon, brand-green outline, and debounced callback.
 * @param {function(string): void} callback - Called with trimmed keyword after debounce (300ms default).
 */
export default function CustomSearchField({
    callback,
    initialValue,
    placeholder,
    sx,
    id = "custom-search-field",
    debounceMs = 300,
    ...rest
}) {
    const [keyword, setKeyword] = useState(initialValue ?? "");

    useEffect(() => {
        setKeyword(initialValue ?? "");
    }, [initialValue]);

    const debouncedCallback = useMemo(
        () =>
            debounce((value) => {
                callback(typeof value === "string" ? value.trim() : "");
            }, debounceMs),
        [callback, debounceMs],
    );

    useEffect(
        () => () => {
            debouncedCallback.cancel();
        },
        [debouncedCallback],
    );

    const handleChange = (e) => {
        const value = e.target.value;
        setKeyword(value);
        if (debounceMs <= 0) {
            callback(typeof value === "string" ? value.trim() : "");
            return;
        }
        debouncedCallback(value);
    };

    return (
        <TextField
            id={id}
            size="small"
            variant="outlined"
            value={keyword}
            onChange={handleChange}
            placeholder={placeholder}
            sx={{ ...greenOutlinedSx, ...sx }}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: BRAND_GREEN }} />
                        </InputAdornment>
                    ),
                },
            }}
            {...rest}
        />
    );
}

CustomSearchField.propTypes = {
    callback: PropTypes.func.isRequired,
    initialValue: PropTypes.string,
    placeholder: PropTypes.string,
    sx: PropTypes.object,
    id: PropTypes.string,
    debounceMs: PropTypes.number,
};
